<template>
  <div class="search-result-page">
    <!-- 顶部搜索横幅 -->
    <div class="search-banner">
      <div class="banner-content">
        <h1 class="banner-title">欢迎使用峰岭科技服务与支持</h1>
        <div class="search-box">
          <el-input
            v-model="innerKeyword"
            placeholder="请输入搜索关键词"
            prefix-icon="el-icon-search"
            clearable
            @keyup.enter.native="doSearch"
            @clear="handleClear"
          >
            <template slot="append">
              <el-button type="primary" @click.native.stop="doSearch">搜索</el-button>
            </template>
          </el-input>
        </div>
        <div v-if="searched" class="result-summary">
          共找到 <span class="kw-red">{{ countVo.total }}</span> 条与
          "<span class="kw-red">{{ displayKeyword }}</span>"相关的结果
        </div>
      </div>
    </div>

    <!-- 类型 Tab 过滤 -->
    <div class="result-container">
      <div class="type-tabs">
        <div
          v-for="tab in tabs"
          :key="tab.type"
          class="tab-item"
          :class="{ active: activeType === tab.type }"
          @click="switchType(tab.type)"
        >
          {{ tab.label }}<span class="tab-count">({{ tab.count }})</span>
        </div>
      </div>

      <!-- 列表 -->
      <div v-loading="loading" class="result-list">
        <template v-if="list.length > 0">
          <div
            v-for="item in list"
            :key="item.id"
            class="result-item"
            @click="handleItemClick(item)"
          >
            <!-- 左侧图标 -->
            <div class="item-icon">
              <img v-if="typeIconMap[item.type].isImg" :src="typeIconMap[item.type].src" class="icon-img" />
              <i v-else :class="typeIconMap[item.type].src" class="icon-el"></i>
            </div>

            <!-- 中间内容 -->
            <div class="item-body">
              <div class="item-title" v-html="highlightText(item.title)"></div>
              <div v-if="item.content" class="item-desc">{{ stripHtml(item.content) }}</div>
              <div class="item-meta">
                <el-tag size="mini" class="type-tag">{{ typeTagMap[item.type] }}</el-tag>
                <span class="meta-time">更新时间：{{ formatDate(item.createTime) }}</span>
              </div>
            </div>

            <!-- 右侧操作 -->
            <div class="item-action">
              <span class="action-btn">查看详情 <i class="el-icon-arrow-right"></i></span>
            </div>
          </div>
        </template>
        <el-empty v-else-if="searched && !loading" description="暂无相关内容" />
      </div>

      <!-- 分页 -->
      <div v-if="total > 0" class="pagination-wrap">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { serviceSearch, serviceCount } from "@/assets/API/service";

export default {
  name: "SearchResultPage",

  props: {
    initKeyword: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      innerKeyword: this.initKeyword || "",
      displayKeyword: this.initKeyword || "",
      activeType: "all",
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      searched: false,
      countVo: {
        total: 0,
        manualCount: 0,
        pdfCount: 0,
        quesCount: 0,
        newsCount: 0,
        mediaCount: 0,
      },
    };
  },

  computed: {
    tabs() {
      return [
        { type: "all",    label: this.$t("Service.allTitle"),       count: this.countVo.total },
        { type: "manual", label: this.$t("Service.videoTitle"),   count: this.countVo.manualCount },
        { type: "pdf",    label: this.$t("Service.downloadTitle"),   count: this.countVo.pdfCount },
        { type: "ques",   label: this.$t("Service.faqTitle"),   count: this.countVo.quesCount },
        { type: "news",   label: this.$t("Service.newsTitle"),   count: this.countVo.newsCount },
        { type: "media",  label: this.$t("Service.mediaTitle"), count: this.countVo.mediaCount },
      ];
    },

    typeIconMap() {
      return {
        manual: { isImg: true,  src: require("@/assets/image/video1.png") },
        pdf:    { isImg: true,  src: require("@/assets/image/download1.png") },
        news:   { isImg: true,  src: require("@/assets/image/update.png") },
        media:  { isImg: true,  src: require("@/assets/image/video.png") },
        ques:   { isImg: false, src: "el-icon-question" },
      };
    },

    typeTagMap() {
      return {
        manual: this.$t("Service.videoTitle"),
        pdf:    this.$t("Service.downloadTitle"),
        ques:   this.$t("Service.faqTitle"),
        news:   this.$t("Service.newsTitle"),
        media:  this.$t("Service.mediaTitle"),
      };
    },
  },

  watch: {
    initKeyword(val) {
      this.innerKeyword = val;
      this.displayKeyword = val;
      this.currentPage = 1;
      this.doSearch();
    },
  },

  mounted() {
    if (this.innerKeyword) {
      this.doSearch();
    }
  },

  methods: {
    async doSearch() {
      const kw = this.innerKeyword ? this.innerKeyword.trim() : "";
      if (!kw) {
        this.$message.warning("请输入搜索关键词");
        return;
      }
      this.displayKeyword = kw;
      this.currentPage = 1;
      this.activeType = "all";
      await Promise.all([this.fetchList(), this.fetchCount()]);
    },

    handleClear() {
      this.innerKeyword = "";
    },

    switchType(type) {
      this.activeType = type;
      this.currentPage = 1;
      this.fetchList();
    },

    async fetchList() {
      this.loading = true;
      try {
        const param = {
          keyword: this.displayKeyword,
          type: this.activeType === "all" ? "" : this.activeType,
          currentPage: this.currentPage,
          pageSize: this.pageSize,
        };
        const res = await serviceSearch(param);
        if (res && res.data) {
          this.list = res.data.records || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("搜索失败", e);
      } finally {
        this.loading = false;
        this.searched = true;
      }
    },

    async fetchCount() {
      try {
        const res = await serviceCount(this.displayKeyword);
        if (res && res.data) {
          this.countVo = res.data;
        }
      } catch (e) {
        console.error("获取数量失败", e);
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleItemClick(item) {
      this.$emit("navigate", item.type, item.id);
    },

    // 关键词高亮：匹配文字显示红色
    highlightText(text) {
      if (!text || !this.displayKeyword) return text;
      const kw = this.displayKeyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const reg = new RegExp(`(${kw})`, "gi");
      return text.replace(reg, '<span class="kw-highlight">$1</span>');
    },

    // 去除 HTML 标签取纯文本
    stripHtml(html) {
      if (!html) return "";
      return html.replace(/<[^>]+>/g, "");
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.search-result-page {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 横幅 */
.search-banner {
  background: linear-gradient(135deg, #e8f4ff 0%, #f0f8ff 100%);
  padding: 40px 0 30px;
  text-align: center;
  border-bottom: 1px solid #e4e9f0;

  .banner-content {
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .banner-title {
    font-size: 22px;
    font-weight: 600;
    color: #1a2b4b;
    margin-bottom: 20px;
  }

  .search-box {
    ::v-deep .el-input__inner {
      border-radius: 4px 0 0 4px;
      height: 44px;
      font-size: 15px;
    }
    ::v-deep .el-input-group__append {
      background: #1890ff;
      border-color: #1890ff;
      .el-button {
        color: #fff;
        padding: 0 24px;
        font-size: 15px;
      }
    }
  }

  .result-summary {
    margin-top: 14px;
    font-size: 14px;
    color: #666;
    .kw-red {
      color: #e63333;
      font-weight: 600;
    }
  }
}

/* 内容区 */
.result-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 20px 40px;
}

/* Tabs */
.type-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .tab-item {
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;
    color: #555;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.2s;

    .tab-count {
      color: #999;
      font-size: 13px;
      margin-left: 2px;
    }

    &:hover {
      background: #e8f4ff;
      color: #1890ff;
    }

    &.active {
      background: #1890ff;
      color: #fff;
      border-color: #1890ff;
      .tab-count {
        color: rgba(255, 255, 255, 0.85);
      }
    }
  }
}

/* 结果列表 */
.result-list {
  min-height: 200px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 10px;
  cursor: pointer;
  border: 1px solid #eef0f5;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: #c8deff;

    .action-btn {
      color: #1890ff;
    }
  }
}

/* 图标 */
.item-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f0f5ff;

  .icon-img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  .icon-el {
    font-size: 24px;
    color: #fa8c16;
  }
}

/* 内容 */
.item-body {
  flex: 1;
  min-width: 0;

  .item-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a2b4b;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    /* 匹配关键词显示红色 */
    ::v-deep .kw-highlight {
      color: #e63333;
      font-weight: 700;
    }
  }

  .item-desc {
    font-size: 13px;
    color: #999;
    line-height: 1.5;
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-meta {
    display: flex;
    align-items: center;
    gap: 10px;

    .type-tag {
      border-radius: 4px;
    }

    .meta-time {
      font-size: 12px;
      color: #bbb;
    }
  }
}

/* 操作按钮 */
.item-action {
  flex-shrink: 0;

  .action-btn {
    font-size: 13px;
    color: #1890ff;
    white-space: nowrap;
    transition: color 0.2s;
  }
}

/* 分页 */
.pagination-wrap {
  display: flex;
  justify-content: center;
  margin-top: 32px;

  ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
    background-color: #1890ff;
  }
}
</style>
